import { ConfirmationModal } from "components/CrmTableDropdown/ConfirmationModal";
import { Button } from "components/DesignSystem/Button/Button";
import { TableUI } from "components/design/TableUI";
import { EditPencil } from "components/icons/EditPencil";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useMyPermissions } from "hooks/useMyPermissions";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  Role,
  useDeleteRoleMutation,
  useGetRoleDetailQuery,
  useGetRolesQuery,
} from "store/apis/roles";
import { CreateEditRoleModal } from "./CreateEditRoleModal";

const ROLE_DESCRIPTIONS: Record<string, string> = {
  SUPER_ADMIN: "Full access to the platform, can manage users and settings",
  STANDARD: "Full access to the bookkeeping platform",
  VIEW_ONLY: "Read-only access to the entire platform",
};

const formatPermissionKey = (key: string) =>
  key
    .split("_")
    .map((w) => w[0] + w.slice(1).toLowerCase())
    .join(" ");

const CustomRoleDescription = ({
  roleId,
  groupId,
}: {
  roleId: string;
  groupId: string;
}) => {
  const { data: roleDetail } = useGetRoleDetailQuery({ groupId, roleId });

  const accessList =
    roleDetail?.permissions
      .filter((p) => p.access_level !== "NO_ACCESS")
      .map((p) => formatPermissionKey(p.permission_key)) ?? [];

  const text =
    accessList.length > 0
      ? `Access to ${accessList.join(", ")}`
      : "No permissions configured";

  return <span className="t-text-body-sm t-text-text-30">{text}</span>;
};

const RoleActions = ({
  role,
  onEdit,
}: {
  role: Role;
  onEdit: (role: Role) => void;
}) => {
  return (
    <div className="t-flex t-items-center t-gap-2 t-justify-end">
      <Button
        size="small"
        disabled={role.is_predefined}
        onClick={() => onEdit(role)}
        className="t-w-24"
      >
        <span className="t-flex t-items-center t-justify-center t-gap-1.5">
          <EditPencil />
          Manage
        </span>
      </Button>
    </div>
  );
};

export const ManageRoles = ({
  triggerCreate,
  onTriggerConsumed,
}: {
  triggerCreate?: boolean;
  onTriggerConsumed?: () => void;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const { isSuperAdmin } = useMyPermissions();
  const { data: roles = [], isLoading } = useGetRolesQuery(
    { groupId: groupId! },
    { skip: !groupId }
  );
  const [deleteRole, { isLoading: deleting }] = useDeleteRoleMutation();

  const createModal = useModal();
  const deleteModal = useModal();

  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const { data: roleDetail } = useGetRoleDetailQuery(
    { groupId: groupId!, roleId: selectedRole?.uuid! },
    { skip: !groupId || !selectedRole }
  );

  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    createModal.open();
  };

  const handleDelete = (role: Role) => {
    setRoleToDelete(role);
    deleteModal.open();
  };

  const handleCloseCreate = () => {
    setSelectedRole(null);
    createModal.close();
    onTriggerConsumed?.();
  };

  const handleCloseDelete = () => {
    setRoleToDelete(null);
    deleteModal.close();
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      await deleteRole({
        groupId: groupId!,
        roleId: roleToDelete.uuid,
      }).unwrap();
      successToast({
        message: `Role "${roleToDelete.name}" deleted successfully`,
      });
    } catch (err: any) {
      alertToast({
        message: err?.data?.error?.message || "Failed to delete role",
      });
    }
  };

  const columnHelper = createColumnHelper<Role>();

  const columns = [
    columnHelper.accessor("name", {
      header: "Role",
      size: 50,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="t-flex t-flex-col t-gap-1">
            <div className="t-flex t-items-center t-gap-2">
              <span className="t-text-body t-font-medium t-text-text-100">
                {info.getValue()}
              </span>
              {!row.is_predefined && (
                <span className="t-text-body-sm t-font-medium t-text-blue-80 t-bg-blue-10 t-px-2 t-py-0.5 t-rounded-full">
                  Custom
                </span>
              )}
            </div>
            {row.is_predefined ? (
              <span className="t-text-body-sm t-text-text-30">
                {ROLE_DESCRIPTIONS[row.role_type]}
              </span>
            ) : (
              <CustomRoleDescription roleId={row.uuid} groupId={groupId!} />
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("user_count", {
      header: () => <div className="t-text-center">No. of Users</div>,
      size: 25,
      cell: (info) => (
        <div className="t-text-center">
          <span className="t-text-body t-text-text-100">{info.getValue()}</span>
        </div>
      ),
    }),
    ...(isSuperAdmin
      ? [
          columnHelper.display({
            id: "actions",
            header: "",
            size: 25,
            cell: (info) => (
              <RoleActions role={info.row.original} onEdit={handleEdit} />
            ),
          }),
        ]
      : []),
  ];

  const table = useReactTable({
    data: roles,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 1 },
  });

  return (
    <div>
      <TableUI table={table} isLoading={isLoading} />
      <CreateEditRoleModal
        open={createModal.isOpen || Boolean(triggerCreate)}
        onClose={handleCloseCreate}
        role={selectedRole && roleDetail ? roleDetail : null}
        userCount={selectedRole?.user_count}
        onDelete={
          selectedRole && !selectedRole.is_predefined
            ? () => {
                handleCloseCreate();
                handleDelete(selectedRole);
              }
            : undefined
        }
      />
      {roleToDelete && (
        <ConfirmationModal
          show={deleteModal.isOpen}
          close={handleCloseDelete}
          action="Delete"
          actionOn="group"
          handleClick={handleConfirmDelete}
          isLoading={deleting}
          title={`Delete "${roleToDelete.name}"?`}
          body={
            <>
              Are you sure you want to delete{" "}
              <span className="t-font-semibold">{roleToDelete.name}</span>? This
              action cannot be undone.
            </>
          }
        />
      )}
    </div>
  );
};
